import { supabase } from '../lib/supabase';

/**
 * Audit Logging Service
 * Tracks all critical operations for compliance and security monitoring
 */

/**
 * Create an audit log entry
 * @param {Object} logData - Audit log data
 * @returns {Object} Result with data or error
 */
export const createAuditLog = async (logData) => {
  try {
    const {
      eventType,
      eventCategory,
      userEmail,
      entityType,
      entityId,
      entityName,
      actionDescription,
      oldValues = null,
      newValues = null,
      dealershipId = null,
      applicationId = null,
      metadata = null,
      severity = 'info'
    } = logData;

    const { data, error } = await supabase
      ?.from('audit_logs')
      ?.insert({
        event_type: eventType,
        event_category: eventCategory,
        user_email: userEmail,
        entity_type: entityType,
        entity_id: entityId,
        entity_name: entityName,
        action_description: actionDescription,
        old_values: oldValues,
        new_values: newValues,
        dealership_id: dealershipId,
        application_id: applicationId,
        metadata: metadata,
        severity: severity
      })
      ?.select()
      ?.single();

    if (error) {
      console.error('Error creating audit log:', error);
      return { error };
    }

    return { data };
  } catch (error) {
    console.error('Exception in createAuditLog:', error);
    return { error };
  }
};

/**
 * Fetch audit logs with filters
 * @param {Object} filters - Filter options
 * @returns {Array} Audit logs
 */
export const fetchAuditLogs = async (filters = {}) => {
  try {
    let query = supabase
      ?.from('audit_logs')
      ?.select(`
        *,
        dealerships (id, name),
        applications (id, customer_name, customer_email)
      `)
      ?.order('created_at', { ascending: false });

    // Apply filters
    if (filters?.eventType) {
      query = query?.eq('event_type', filters?.eventType);
    }
    if (filters?.eventCategory) {
      query = query?.eq('event_category', filters?.eventCategory);
    }
    if (filters?.entityType) {
      query = query?.eq('entity_type', filters?.entityType);
    }
    if (filters?.severity) {
      query = query?.eq('severity', filters?.severity);
    }
    if (filters?.userEmail) {
      query = query?.eq('user_email', filters?.userEmail);
    }
    if (filters?.dealershipId) {
      query = query?.eq('dealership_id', filters?.dealershipId);
    }
    if (filters?.dateFrom) {
      query = query?.gte('created_at', filters?.dateFrom);
    }
    if (filters?.dateTo) {
      query = query?.lte('created_at', filters?.dateTo);
    }
    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching audit logs:', error);
      return { data: [], error };
    }

    // Convert to camelCase
    const formattedLogs = data?.map(log => ({
      id: log?.id,
      eventType: log?.event_type,
      eventCategory: log?.event_category,
      userId: log?.user_id,
      userEmail: log?.user_email,
      entityType: log?.entity_type,
      entityId: log?.entity_id,
      entityName: log?.entity_name,
      actionDescription: log?.action_description,
      oldValues: log?.old_values,
      newValues: log?.new_values,
      ipAddress: log?.ip_address,
      userAgent: log?.user_agent,
      dealershipId: log?.dealership_id,
      dealershipName: log?.dealerships?.name || null,
      applicationId: log?.application_id,
      applicationCustomerName: log?.applications?.customer_name || null,
      metadata: log?.metadata,
      severity: log?.severity,
      createdAt: log?.created_at
    })) || [];

    return { data: formattedLogs, error: null };
  } catch (error) {
    console.error('Exception in fetchAuditLogs:', error);
    return { data: [], error };
  }
};

/**
 * Log email sent event
 * @param {Object} emailData - Email details
 */
export const logEmailSent = async (emailData) => {
  const { to, subject, templateType, applicationId = null, dealershipId = null, userEmail = 'system' } = emailData;

  return await createAuditLog({
    eventType: 'email_sent',
    eventCategory: 'system',
    userEmail: userEmail,
    entityType: 'email',
    entityId: applicationId || dealershipId,
    entityName: to,
    actionDescription: `Email sent: ${subject}`,
    newValues: {
      to: to,
      subject: subject,
      template_type: templateType
    },
    dealershipId: dealershipId,
    applicationId: applicationId,
    metadata: {
      template_type: templateType,
      recipient: to
    },
    severity: 'info'
  });
};

/**
 * Log bulk action
 * @param {Object} bulkActionData - Bulk action details
 */
export const logBulkAction = async (bulkActionData) => {
  const { actionType, entityType, entityIds, userEmail, description, metadata = {} } = bulkActionData;

  return await createAuditLog({
    eventType: 'bulk_action_executed',
    eventCategory: 'data_modification',
    userEmail: userEmail,
    entityType: entityType,
    entityId: null,
    entityName: `Bulk ${actionType}`,
    actionDescription: description,
    newValues: {
      action_type: actionType,
      affected_count: entityIds?.length
    },
    metadata: {
      ...metadata,
      bulk_action: true,
      entity_ids: entityIds,
      action_type: actionType
    },
    severity: 'info'
  });
};

/**
 * Log document upload
 * @param {Object} documentData - Document details
 */
export const logDocumentUpload = async (documentData) => {
  const { fileName, fileSize, applicationId, dealershipId, userEmail } = documentData;

  return await createAuditLog({
    eventType: 'document_uploaded',
    eventCategory: 'data_modification',
    userEmail: userEmail,
    entityType: 'document',
    entityId: applicationId,
    entityName: fileName,
    actionDescription: `Document uploaded: ${fileName}`,
    newValues: {
      file_name: fileName,
      file_size: fileSize
    },
    dealershipId: dealershipId,
    applicationId: applicationId,
    metadata: {
      file_name: fileName,
      file_size: fileSize
    },
    severity: 'info'
  });
};

/**
 * Log document view
 * @param {Object} documentData - Document details
 */
export const logDocumentView = async (documentData) => {
  const { fileName, applicationId, dealershipId, userEmail } = documentData;

  return await createAuditLog({
    eventType: 'document_viewed',
    eventCategory: 'security',
    userEmail: userEmail,
    entityType: 'document',
    entityId: applicationId,
    entityName: fileName,
    actionDescription: `Document viewed: ${fileName}`,
    dealershipId: dealershipId,
    applicationId: applicationId,
    metadata: {
      file_name: fileName
    },
    severity: 'info'
  });
};

/**
 * Log authentication events
 * @param {Object} authData - Authentication details
 */
export const logAuthEvent = async (authData) => {
  const { eventType, userEmail, success, metadata = {} } = authData;

  return await createAuditLog({
    eventType: success ? 'login_success' : 'login_failed',
    eventCategory: 'security',
    userEmail: userEmail,
    entityType: 'user',
    entityId: null,
    entityName: userEmail,
    actionDescription: success ? `User logged in: ${userEmail}` : `Failed login attempt: ${userEmail}`,
    metadata: metadata,
    severity: success ? 'info' : 'warning'
  });
};

/**
 * Get audit log statistics
 * @param {Object} filters - Filter options
 * @returns {Object} Statistics
 */
export const getAuditLogStats = async (filters = {}) => {
  try {
    const { data: logs, error } = await fetchAuditLogs(filters);

    if (error) {
      return { error };
    }

    const stats = {
      total: logs?.length || 0,
      byCategory: {},
      bySeverity: {},
      byEventType: {},
      recentCritical: logs?.filter(log => log?.severity === 'critical')?.slice(0, 10) || []
    };

    logs?.forEach(log => {
      // Count by category
      stats.byCategory[log?.eventCategory] = (stats?.byCategory?.[log?.eventCategory] || 0) + 1;
      
      // Count by severity
      stats.bySeverity[log?.severity] = (stats?.bySeverity?.[log?.severity] || 0) + 1;
      
      // Count by event type
      stats.byEventType[log?.eventType] = (stats?.byEventType?.[log?.eventType] || 0) + 1;
    });

    return { data: stats, error: null };
  } catch (error) {
    console.error('Exception in getAuditLogStats:', error);
    return { error };
  }
};